import { useAppSelector } from "../hooks/reduxToolkit";
import type { RootState } from "../reduxToolkit/store";
import { PLAID_HSA_LINK_FEATURE_FLAG } from "../reduxToolkit/plaidSlice";

/**
 * Map subscription tiers to their feature flags.
 * This is the single source of truth for what features each tier includes.
 *
 * Mirrors the backend's `StripeService::getFeatureFlagsForTier()`, which reads
 * `services.stripe.prices[*].feature_flags`
 * (apps/reimbursement-tracker-admin/config/services.php). Keep all three in sync
 * when changing which features a tier grants.
 *
 * Solo and family differ by who you can submit for (see lib/rules/requiredPlan.ts),
 * not by features. Basic and premium are legacy tiers kept for existing subscribers.
 */
const TIER_FEATURES: Record<string, string[]> = {
  basic: [],
  premium: ['alice_ai', 'plaid_hsa_link', 'ai_phone_calling'],
  solo: ['alice_ai', 'plaid_hsa_link', 'send_alice_intro_email', 'ai_phone_calling'],
  family: ['alice_ai', 'plaid_hsa_link', 'send_alice_intro_email', 'ai_phone_calling'],
}

/**
 * Features switched off for everyone, regardless of organization flags,
 * subscription tier, or the pre-signup trial grant below.
 *
 * Empty today. Kept as the emergency mechanism for switching a feature off
 * everywhere without unpicking tier and organization flag data.
 */
const DISABLED_FEATURES: readonly string[] = []

/**
 * Get the feature flags for a given subscription tier.
 */
function getFeaturesForTier(tier: string | null | undefined): string[] {
  if (!tier) return []
  return TIER_FEATURES[tier] || []
}

/**
 * Pure feature-flag check against a membership slice — the non-hook form of
 * `useFeature`, usable outside React (e.g. from the product tour, which reads
 * `store.getState()`).
 *
 * Feature flags can come from three sources:
 * - Pre-signup: users with no organization and no subscription have access to all features (trial)
 * - Organization: flags set on the user's organization (for org-provided users)
 * - Subscription: flags derived from the user's individual Stripe subscription tier
 */
export function selectHasFeature(
  membership: RootState['membership'] | undefined,
  feature: string,
): boolean {
  // Checked before the pre-signup grant below, which would otherwise hand
  // trial users every feature including the disabled ones.
  if (DISABLED_FEATURES.includes(feature)) {
    return false
  }

  const orgFlags = membership?.organization?.feature_flags || []

  const hasOrganization = !!membership?.organization
  const hasSubscription = !!membership?.subscription

  // Pre-signup users (no org, no subscription) get access to all features
  if (!hasOrganization && !hasSubscription) {
    return true
  }

  const subFlags = getFeaturesForTier(membership?.subscription?.tier)
  return orgFlags.includes(feature) || subFlags.includes(feature)
}

/**
 * Checks the given feature flag to see if it is enabled for this user.
 * See `selectHasFeature` for the resolution rules.
 */
export function useFeature(feature: string): boolean {
  return useAppSelector(s => selectHasFeature(s.membership, feature))
}

/**
 * Whether the current user may link a Plaid bank/HSA account.
 *
 * Deliberately uses `useFeature` (not `useOrgOrTierFeature`) so that pre-signup
 * trial users — who have no organization and no subscription — are also granted
 * access. Linking a real bank account is high-friction enough that we're
 * comfortable exposing it during the trial. Flag-less Basic/org users stay
 * blocked (the button renders disabled with a "contact us to enable sync"
 * tooltip at the two link surfaces).
 *
 * Mirrors the backend gate in `PlaidController::membershipCanLink`. Keep the two
 * in sync when changing who can link.
 */
export function usePlaidLinkEnabled(): boolean {
  return useFeature(PLAID_HSA_LINK_FEATURE_FLAG)
}
